import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Select, MenuItem, FormControl, InputLabel,
  Button, Chip, Grid, Card, CardContent, CircularProgress, Alert,
  Divider, Tabs, Tab, ToggleButton, ToggleButtonGroup, IconButton, Tooltip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { useQuery, useQueryClient } from 'react-query';
import { reportsAPI, statusAPI, brokersAPI, mt5API, customersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { REPORTS_SUB_TABS, REPORTS_TAB_ORDER } from '../constants/viewerPermissions';

const formatMoney = (v) => {
  if (v == null || v === '') return '-';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// ─── Tab Nạp/Rút ──────────────────────────────────────────────────────────────

function TransactionsTab() {
  const { canAccountSeeReport } = useAuth();
  const [filters, setFilters] = useState({
    from: firstOfMonth(),
    to: today(),
    account_id: '',
    customer_id: '',
    type: '',
    is_internal: '',
    page: 1,
    limit: 50,
  });
  const [applied, setApplied] = useState(filters);
  const [fetchKey, setFetchKey] = useState(0);

  const { data: accountsData } = useQuery('accounts-list', () =>
    statusAPI.getAccounts().then(r => r.data)
  );

  const { data: customerOptions = [] } = useQuery('customer-options', () =>
    customersAPI.getOptions().then(r => r.data).catch(() => [])
  );

  const { data, isLoading, isError, isFetching } = useQuery(
    ['transactions', applied, fetchKey],
    () => reportsAPI.getTransactions({
      from: applied.from || undefined,
      to: applied.to || undefined,
      account_id: applied.account_id || undefined,
      customer_id: applied.customer_id || undefined,
      type: applied.type || undefined,
      is_internal: applied.is_internal !== '' ? applied.is_internal : undefined,
      page: applied.page,
      limit: applied.limit,
    }),
    { keepPreviousData: true }
  );

  const transactions = data?.data?.transactions || [];
  const total = data?.data?.total || 0;
  const summary = data?.data?.summary || {};
  const totalPages = Math.ceil(total / applied.limit);

  const handleSearch = () => {
    setApplied({ ...filters, page: 1 });
    setFetchKey(k => k + 1);
  };

  const handleExportCSV = () => {
    if (!transactions.length) return;
    const headers = ['Thời gian', 'Tài khoản', 'Tên', 'Loại', 'Số tiền', 'Số dư sau', 'Ghi chú'];
    const rows = transactions.map(t => [
      new Date(t.deal_time).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }),
      t.mt5_account_id,
      t.client_name || '-',
      t.is_internal ? (t.type === 'deposit' ? 'Nạp nội bộ' : 'Rút nội bộ') : (t.type === 'deposit' ? 'Nạp' : 'Rút'),
      t.amount,
      t.balance_after,
      t.comment || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${applied.from}_${applied.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Viewers may have a per-account override that hides this report → drop them
  // from the dropdown so they can't pick an account whose data the server will reject.
  const accounts = (accountsData?.accounts || []).filter(a => canAccountSeeReport(a.mt5_account_id, 'transactions'));

  return (
    <>
      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg,#22c55e20,#16a34a10)', border: '1px solid #22c55e40' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Tổng nạp</Typography>
              <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700 }}>
                ${formatMoney(summary.total_deposit || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">{summary.deposit_count || 0} giao dịch</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg,#ef444420,#dc262610)', border: '1px solid #ef444440' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Tổng rút</Typography>
              <Typography variant="h6" sx={{ color: '#ef4444', fontWeight: 700 }}>
                ${formatMoney(summary.total_withdrawal || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">{summary.withdrawal_count || 0} giao dịch</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ background: 'linear-gradient(135deg,#3b82f620,#2563eb10)', border: '1px solid #3b82f640' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Ròng (Nạp - Rút)</Typography>
              <Typography variant="h6" sx={{ color: '#3b82f6', fontWeight: 700 }}>
                ${formatMoney((summary.total_deposit || 0) - (summary.total_withdrawal || 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Tổng giao dịch</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{total}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" type="date" label="Từ ngày"
              InputLabelProps={{ shrink: true }}
              value={filters.from}
              onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth size="small" type="date" label="Đến ngày"
              InputLabelProps={{ shrink: true }}
              value={filters.to}
              onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tài khoản</InputLabel>
              <Select value={filters.account_id} label="Tài khoản"
                onChange={e => setFilters(f => ({ ...f, account_id: e.target.value }))}>
                <MenuItem value="">-- Tất cả --</MenuItem>
                {accounts.map(a => (
                  <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                    {a.client_name ? `${a.client_name} (${a.mt5_account_id})` : a.mt5_account_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {customerOptions.length > 0 && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Khách hàng</InputLabel>
                <Select value={filters.customer_id} label="Khách hàng"
                  onChange={e => setFilters(f => ({ ...f, customer_id: e.target.value }))}>
                  <MenuItem value="">-- Tất cả --</MenuItem>
                  {customerOptions.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Loại</InputLabel>
              <Select value={filters.type} label="Loại"
                onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}>
                <MenuItem value="">-- Tất cả --</MenuItem>
                <MenuItem value="deposit">Nạp tiền</MenuItem>
                <MenuItem value="withdrawal">Rút tiền</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Nguồn</InputLabel>
              <Select value={filters.is_internal} label="Nguồn"
                onChange={e => setFilters(f => ({ ...f, is_internal: e.target.value }))}>
                <MenuItem value="">-- Tất cả --</MenuItem>
                <MenuItem value="false">Bên ngoài</MenuItem>
                <MenuItem value="true">Nội bộ</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button fullWidth variant="contained" onClick={handleSearch} disabled={isLoading || isFetching}>
              Lọc
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button fullWidth variant="outlined" onClick={handleExportCSV} disabled={!transactions.length}>
              Xuất CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        {isLoading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}
        {isError && <Alert severity="error" sx={{ m: 2 }}>Không thể tải dữ liệu</Alert>}
        {!isLoading && !isError && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: 'rgb(15, 21, 53)', color: 'white', width: 48 }}>STT</TableCell>
                    <TableCell sx={{ backgroundColor: 'rgb(15, 21, 53)', color: 'white' }}>Thời gian</TableCell>
                    <TableCell sx={{ backgroundColor: 'rgb(15, 21, 53)', color: 'white' }}>Tài khoản</TableCell>
                    <TableCell sx={{ backgroundColor: 'rgb(15, 21, 53)', color: 'white' }}>Tên</TableCell>
                    <TableCell sx={{ backgroundColor: 'rgb(15, 21, 53)', color: 'white' }}>Loại</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: 'rgb(15, 21, 53)', color: 'white' }}>Số tiền</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: 'rgb(15, 21, 53)', color: 'white' }}>Số dư sau</TableCell>
                    <TableCell sx={{ backgroundColor: 'rgb(15, 21, 53)', color: 'white' }}>Ghi chú</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Không có giao dịch nào trong khoảng thời gian này
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((t, idx) => (
                      <TableRow key={t.id} hover>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                          {(applied.page - 1) * applied.limit + idx + 1}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {new Date(t.deal_time).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
                        </TableCell>
                        <TableCell>{t.mt5_account_id}</TableCell>
                        <TableCell>{t.client_name || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              t.is_internal
                                ? (t.type === 'deposit' ? 'Nạp nội bộ' : 'Rút nội bộ')
                                : (t.type === 'deposit' ? 'Nạp' : 'Rút')
                            }
                            color={t.type === 'deposit' ? 'success' : 'error'}
                            variant={t.is_internal ? 'outlined' : 'filled'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: t.type === 'deposit' ? '#22c55e' : '#ef4444' }}>
                          {t.type === 'deposit' ? '+' : '-'}${formatMoney(t.amount)}
                        </TableCell>
                        <TableCell align="right">${formatMoney(t.balance_after)}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{t.comment || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, p: 2 }}>
                  <Button size="small" disabled={applied.page <= 1}
                    onClick={() => setApplied(a => ({ ...a, page: a.page - 1 }))}>
                    ← Trước
                  </Button>
                  <Typography variant="body2">
                    Trang {applied.page} / {totalPages} ({total} giao dịch)
                  </Typography>
                  <Button size="small" disabled={applied.page >= totalPages}
                    onClick={() => setApplied(a => ({ ...a, page: a.page + 1 }))}>
                    Tiếp →
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  );
}

// ─── Quick period presets ──────────────────────────────────────────────────────
const PRESETS = [
  { label: 'Hôm nay', key: 'today' },
  { label: 'Tuần này', key: 'this_week' },
  { label: 'Tuần trước', key: 'last_week' },
  { label: 'Tháng này', key: 'this_month' },
  { label: 'Tháng trước', key: 'last_month' },
  { label: 'Tất cả', key: 'all' },
];

function getPresetDates(key) {
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const now = new Date();
  switch (key) {
    case 'today': return { from: today(), to: today() };
    case 'this_week': {
      const d = new Date(now);
      const day = d.getDay();
      d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      return { from: fmt(d), to: today() };
    }
    case 'last_week': {
      const d = new Date(now);
      const day = d.getDay();
      const thisMonday = new Date(d);
      thisMonday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(thisMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday);
      lastSunday.setDate(thisMonday.getDate() - 1);
      return { from: fmt(lastMonday), to: fmt(lastSunday) };
    }
    case 'this_month': return { from: firstOfMonth(), to: today() };
    case 'last_month': {
      const d = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(d); lastDay.setDate(0);
      const firstDay = new Date(lastDay.getFullYear(), lastDay.getMonth(), 1);
      return { from: fmt(firstDay), to: fmt(lastDay) };
    }
    case 'all': return { from: '', to: '' };
    default: return { from: firstOfMonth(), to: today() };
  }
}

// ─── Palette for chart lines ──────────────────────────────────────────────────
const CHART_COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#14b8a6',
  '#a855f7', '#fb923c', '#64748b', '#10b981', '#e11d48',
];

// ─── ProfitChart ──────────────────────────────────────────────────────────────
function ProfitChart({ applied, fetchKey }) {
  const { data, isLoading } = useQuery(
    ['profit-daily', applied, fetchKey],
    () => reportsAPI.getProfitDaily({
      from: applied.from || undefined,
      to: applied.to || undefined,
      account_id: applied.account_id || undefined,
      customer_id: applied.customer_id || undefined,
      broker_id: applied.broker_id || undefined,
      group_name: applied.group_name || undefined,
    }),
    { keepPreviousData: true }
  );

  const rows = data?.data?.rows || [];

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }
  if (!rows.length) return null;

  // Build chart data: array of { date, [accountId]: dailyProfit, ... }
  const accountMap = {}; // accountId -> client_name
  const dateMap = {};    // date -> { [accountId]: profit }

  for (const r of rows) {
    accountMap[r.mt5_account_id] = r.client_name || r.mt5_account_id;
    if (!dateMap[r.trade_date]) dateMap[r.trade_date] = {};
    dateMap[r.trade_date][r.mt5_account_id] = (dateMap[r.trade_date][r.mt5_account_id] || 0) + r.daily_net_profit;
  }

  const accountIds = Object.keys(accountMap);

  // Generate all calendar dates between min and max to keep line continuous
  const allDates = Object.keys(dateMap).sort();
  const fullDates = [];
  if (allDates.length >= 2) {
    const cur = new Date(allDates[0]);
    const end = new Date(allDates[allDates.length - 1]);
    while (cur <= end) {
      fullDates.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
  } else {
    fullDates.push(...allDates);
  }

  // Fill 0 for accounts with no data on a date so line stays flat and continuous
  const chartData = fullDates.map(date => {
    const entry = { date };
    for (const id of accountIds) {
      entry[id] = dateMap[date]?.[id] ?? 0;
    }
    return entry;
  });
  const isSingleAccount = applied.account_id && accountIds.length === 1;

  // Format tick labels: shorten to MM/DD when range > 30 days, else DD/MM
  const fmtDate = (d) => {
    const [, m, day] = d.split('-');
    return `${day}/${m}`;
  };

  const fmtTooltipValue = (val) => {
    if (val == null) return null;
    const s = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(val));
    return `${val >= 0 ? '+' : '-'}$${s}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <Paper sx={{ p: 1.5, minWidth: 160, border: '1px solid rgba(255,255,255,0.15)' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        {payload.map((p) => (
          <Box key={p.dataKey} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="caption" sx={{ color: p.color }}>{p.name}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: p.value >= 0 ? '#22c55e' : '#ef4444' }}>
              {fmtTooltipValue(p.value)}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
        Biểu đồ LN ròng theo ngày {isSingleAccount ? `— ${accountMap[applied.account_id]}` : '(tất cả tài khoản)'}
      </Typography>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
          <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: '#9ca3af' }} />
          <YAxis
            tickFormatter={(v) => `$${v >= 0 ? '' : '-'}${Math.abs(v) >= 1000 ? (Math.abs(v) / 1000).toFixed(1) + 'k' : Math.abs(v).toFixed(0)}`}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            width={64}
          />
          <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" strokeDasharray="4 2" />
          <ReTooltip content={<CustomTooltip />} />
          {accountIds.length > 1 && (
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={(val) => accountMap[val] || val}
            />
          )}
          {accountIds.map((id, i) => (
            <Line
              key={id}
              type="monotone"
              dataKey={id}
              name={accountMap[id] || id}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth={isSingleAccount ? 2.5 : 1.8}
              dot={chartData.length <= 14}
              activeDot={{ r: 4 }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}

// ─── Tab Lợi nhuận ─────────────────────────────────────────────────────────────

function ProfitTab() {
  const { canAccountSeeReport } = useAuth();
  const [filters, setFilters] = useState({ from: firstOfMonth(), to: today(), broker_id: '', group_name: '', account_id: '', customer_id: '' });
  const [applied, setApplied] = useState(filters);
  const [fetchKey, setFetchKey] = useState(0);
  const [activePreset, setActivePreset] = useState('this_month');
  const [view, setView] = useState('summary'); // 'summary' | 'deals'
  const [dealsPage, setDealsPage] = useState(1);

  const { data: brokersData } = useQuery('brokers-list', () =>
    brokersAPI.getAll().then(r => r.data)
  );

  const { data: accountsData } = useQuery('accounts-list-profit', () =>
    statusAPI.getAccounts().then(r => r.data)
  );

  const { data: customerOptions = [] } = useQuery('customer-options-profit', () =>
    customersAPI.getOptions().then(r => r.data).catch(() => [])
  );

  const centAccountIds = useMemo(
    () => new Set((accountsData?.accounts || []).filter(a => a.is_cent).map(a => String(a.mt5_account_id))),
    [accountsData]
  );

  const { data, isLoading, isError, isFetching } = useQuery(
    ['profit-report', applied, fetchKey],
    () => reportsAPI.getProfitReport({
      from: applied.from || undefined,
      to: applied.to || undefined,
      account_id: applied.account_id || undefined,
      customer_id: applied.customer_id || undefined,
      broker_id: applied.broker_id || undefined,
      group_name: applied.group_name || undefined,
    }),
    { keepPreviousData: true }
  );

  // Separate query for groups list — omits group_name so the dropdown always shows all groups
  const { data: groupsData } = useQuery(
    ['profit-groups', applied.from, applied.to, applied.broker_id, applied.account_id, applied.customer_id, fetchKey],
    () => reportsAPI.getProfitReport({
      from: applied.from || undefined,
      to: applied.to || undefined,
      account_id: applied.account_id || undefined,
      customer_id: applied.customer_id || undefined,
      broker_id: applied.broker_id || undefined,
    }),
    { keepPreviousData: true }
  );

  const { data: dealsData, isLoading: dealsLoading } = useQuery(
    ['profit-deals', applied, fetchKey, dealsPage],
    () => reportsAPI.getProfitDeals({
      from: applied.from || undefined,
      to: applied.to || undefined,
      broker_id: applied.broker_id || undefined,
      account_id: applied.account_id || undefined,
      customer_id: applied.customer_id || undefined,
      page: dealsPage,
      limit: 100,
    }),
    { keepPreviousData: true, enabled: view === 'deals' }
  );

  const accounts = [...(data?.data?.accounts || [])].sort((a, b) =>
    (a.client_name || '').localeCompare(b.client_name || '', 'vi', { sensitivity: 'base' })
  );
  const summary = data?.data?.summary || {};
  const deals = dealsData?.data?.deals || [];
  const dealsTotal = dealsData?.data?.total || 0;
  const dealsTotalPages = Math.ceil(dealsTotal / 100);

  // Collect unique group names from unfiltered groups query
  const allGroups = [...new Set((groupsData?.data?.accounts || []).map(a => a.group_name).filter(Boolean))].sort();
  const brokers = brokersData?.brokers || [];
  // Account dropdown filter: viewer's per-account override may forbid this sub-tab.
  const allAccounts = (accountsData?.accounts || []).filter(a => canAccountSeeReport(a.mt5_account_id, 'profit'));

  const handlePreset = (key) => {
    const dates = getPresetDates(key);
    setActivePreset(key);
    setFilters(f => ({ ...f, ...dates }));
    setApplied(a => ({ ...a, ...dates }));
    setFetchKey(k => k + 1);
    setDealsPage(1);
  };

  const handleSearch = () => {
    setActivePreset('');
    setApplied({ ...filters });
    setFetchKey(k => k + 1);
    setDealsPage(1);
  };

  const profitColor = (v) => {
    if (v == null || v === 0) return 'inherit';
    return v > 0 ? '#22c55e' : '#ef4444';
  };

  const fmtProfit = (v, isCent) => {
    if (v == null) return '-';
    const s = formatMoney(Math.abs(v));
    if (s === '-') return '-';
    return isCent ? `${v >= 0 ? '+' : '-'}${s}¢` : `${v >= 0 ? '+' : '-'}$${s}`;
  };

  const handleExportCSV = () => {
    if (view === 'summary') {
      if (!accounts.length) return;
      const headers = ['Tài khoản', 'Tên', 'Nhóm', 'Số lệnh', 'Tỷ lệ LN', 'LN ròng', 'LN Master', 'Thực nhận'];
      const rows = accounts.map(a => [
        a.mt5_account_id, a.client_name || '-', a.group_name || '-',
        a.deal_count || 0,
        a.profit_share > 0 ? `${a.profit_share}%` : '-',
        (a.net_profit || 0).toFixed(2),
        (a.ln_master || 0).toFixed(2),
        (a.thuc_nhan || 0).toFixed(2),
      ]);
      const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `profit_summary_${applied.from}_${applied.to}.csv`; a.click();
      URL.revokeObjectURL(url);
    } else {
      if (!deals.length) return;
      const headers = ['Thời gian đóng', 'Tài khoản', 'Tên', 'Symbol', 'Lots', 'Lợi nhuận', 'Commission', 'Swap', 'Ròng', 'Ghi chú'];
      const rows = deals.map(d => [
        new Date(d.close_time).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }),
        d.mt5_account_id, d.client_name || '-', d.symbol || '-',
        d.lots || 0, d.profit || 0, d.commission || 0, d.swap || 0,
        (d.profit || 0) + (d.commission || 0) + (d.swap || 0),
        d.comment || '',
      ]);
      const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `profit_deals_${applied.from}_${applied.to}.csv`; a.click();
      URL.revokeObjectURL(url);
    }
  };

  const thStyle = { backgroundColor: 'rgb(15, 21, 53)', color: 'white' };

  return (
    <>
      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Tài khoản', value: summary.account_count || 0, plain: true },
          { label: 'Số lệnh', value: summary.deal_count || 0, plain: true },
          {
            label: 'LN ròng', value: summary.net_profit || 0,
            color: profitColor(summary.net_profit),
            bg: (summary.net_profit || 0) >= 0 ? 'linear-gradient(135deg,#22c55e30,#16a34a15)' : 'linear-gradient(135deg,#ef444430,#dc262615)',
            border: (summary.net_profit || 0) >= 0 ? '#22c55e60' : '#ef444460',
          },
          {
            label: 'LN Master', value: summary.ln_master || 0,
            color: profitColor(summary.ln_master),
            bg: 'linear-gradient(135deg,#f59e0b20,#d9770610)',
            border: '#f59e0b40',
          },
          {
            label: 'Thực nhận', value: summary.thuc_nhan || 0,
            color: profitColor(summary.thuc_nhan),
            bg: (summary.thuc_nhan || 0) >= 0 ? 'linear-gradient(135deg,#3b82f620,#2563eb10)' : 'linear-gradient(135deg,#ef444420,#dc262610)',
            border: (summary.thuc_nhan || 0) >= 0 ? '#3b82f640' : '#ef444440',
          },
        ].map((c, i) => (
          <Grid item xs={6} sm={4} md={2.4} key={i}>
            <Card sx={{ background: c.bg || 'rgba(255,255,255,0.03)', border: `1px solid ${c.border || 'rgba(255,255,255,0.08)'}` }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="h6" sx={{ color: c.color || 'inherit', fontWeight: 700, fontSize: '1rem' }}>
                  {c.plain ? c.value : fmtProfit(c.value)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        {/* Quick presets */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {PRESETS.map(p => (
            <Button
              key={p.key}
              size="small"
              variant={activePreset === p.key ? 'contained' : 'outlined'}
              onClick={() => handlePreset(p.key)}
              sx={{ minWidth: 'unset', px: 1.5 }}
            >
              {p.label}
            </Button>
          ))}
        </Box>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Từ ngày"
              InputLabelProps={{ shrink: true }} value={filters.from}
              onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Đến ngày"
              InputLabelProps={{ shrink: true }} value={filters.to}
              onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tài khoản</InputLabel>
              <Select value={filters.account_id} label="Tài khoản"
                onChange={e => setFilters(f => ({ ...f, account_id: e.target.value }))}>
                <MenuItem value="">-- Tất cả --</MenuItem>
                {allAccounts.map(a => (
                  <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                    {a.client_name ? `${a.client_name} (${a.mt5_account_id})` : a.mt5_account_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {customerOptions.length > 0 && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Khách hàng</InputLabel>
                <Select value={filters.customer_id} label="Khách hàng"
                  onChange={e => setFilters(f => ({ ...f, customer_id: e.target.value }))}>
                  <MenuItem value="">-- Tất cả --</MenuItem>
                  {customerOptions.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Nhóm</InputLabel>
              <Select value={filters.group_name} label="Nhóm"
                onChange={e => setFilters(f => ({ ...f, group_name: e.target.value }))}>
                <MenuItem value="">-- Tất cả --</MenuItem>
                {allGroups.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Button fullWidth variant="contained" onClick={handleSearch} disabled={isLoading || isFetching}>Lọc</Button>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Button fullWidth variant="outlined" onClick={handleExportCSV} disabled={view === 'summary' ? !accounts.length : !deals.length}>Xuất CSV</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* View toggle */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
          <ToggleButton value="summary">Tổng hợp theo tài khoản</ToggleButton>
          <ToggleButton value="deals">Chi tiết từng lệnh</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Summary table */}
      {view === 'summary' && (
        <Paper>
          {isLoading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}
          {isError && <Alert severity="error" sx={{ m: 2 }}>Không thể tải dữ liệu</Alert>}
          {!isLoading && !isError && (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thStyle, width: 44 }}>STT</TableCell>
                    <TableCell sx={{ ...thStyle, width: 110 }}>Tài khoản</TableCell>
                    <TableCell sx={{ ...thStyle, width: 140 }}>Tên</TableCell>
                    <TableCell sx={{ ...thStyle, width: 90 }}>Nhóm</TableCell>
                    <TableCell align="right" sx={{ ...thStyle, width: 70 }}>Số lệnh</TableCell>
                    <TableCell align="right" sx={{ ...thStyle, width: 80 }}>Tỷ lệ LN</TableCell>
                    <TableCell align="right" sx={{ ...thStyle, width: 110 }}>LN ròng</TableCell>
                    <TableCell align="right" sx={{ ...thStyle, width: 110 }}>LN Master</TableCell>
                    <TableCell align="right" sx={{ ...thStyle, width: 110 }}>Thực nhận</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Không có dữ liệu lịch sử giao dịch trong khoảng thời gian này
                      </TableCell>
                    </TableRow>
                  ) : accounts.map((a, idx) => {
                    const net = a.net_profit || 0;
                    const lnMaster = a.ln_master || 0;
                    const thucNhan = a.thuc_nhan || 0;
                    const isCent = centAccountIds.has(String(a.mt5_account_id));
                    return (
                    <TableRow key={a.mt5_account_id} hover>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{a.mt5_account_id}</TableCell>
                      <TableCell>{a.client_name || '-'}</TableCell>
                      <TableCell>{a.group_name ? <Chip label={a.group_name} size="small" variant="outlined" /> : '-'}</TableCell>
                      <TableCell align="right">{a.deal_count}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                        {a.profit_share > 0 ? `${a.profit_share}%` : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: profitColor(net) }}>{fmtProfit(net, isCent)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: lnMaster !== 0 ? profitColor(lnMaster) : 'text.secondary' }}>
                        {lnMaster !== 0 ? fmtProfit(lnMaster, isCent) : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: profitColor(thucNhan) }}>
                        {fmtProfit(thucNhan, isCent)}
                      </TableCell>
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Deals detail table */}
      {view === 'deals' && (
        <Paper>
          {dealsLoading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}
          {!dealsLoading && (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thStyle}>Thời gian đóng</TableCell>
                      <TableCell sx={thStyle}>Tài khoản</TableCell>
                      <TableCell sx={thStyle}>Tên</TableCell>
                      <TableCell sx={thStyle}>Symbol</TableCell>
                      <TableCell align="right" sx={thStyle}>Lots</TableCell>
                      <TableCell align="right" sx={thStyle}>Lợi nhuận</TableCell>
                      <TableCell align="right" sx={thStyle}>Commission</TableCell>
                      <TableCell align="right" sx={thStyle}>Swap</TableCell>
                      <TableCell align="right" sx={thStyle}>Ròng</TableCell>
                      <TableCell sx={thStyle}>Ghi chú</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Không có dữ liệu
                        </TableCell>
                      </TableRow>
                    ) : deals.map(d => {
                      const net = (d.profit || 0) + (d.commission || 0) + (d.swap || 0);
                      const isCent = d.symbol ? d.symbol.slice(-1) === 'c' : centAccountIds.has(String(d.mt5_account_id));
                      return (
                        <TableRow key={d.id} hover>
                          <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {new Date(d.close_time).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
                          </TableCell>
                          <TableCell>{d.mt5_account_id}</TableCell>
                          <TableCell>{d.client_name || '-'}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{d.symbol || '-'}</TableCell>
                          <TableCell align="right">{d.lots || 0}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: profitColor(d.profit) }}>{fmtProfit(d.profit, isCent)}</TableCell>
                          <TableCell align="right" sx={{ color: '#f59e0b' }}>{fmtProfit(d.commission, isCent)}</TableCell>
                          <TableCell align="right" sx={{ color: '#8b5cf6' }}>{fmtProfit(d.swap, isCent)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: profitColor(net) }}>{fmtProfit(net, isCent)}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{d.comment || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {dealsTotalPages > 1 && (
                <>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, p: 2 }}>
                    <Button size="small" disabled={dealsPage <= 1} onClick={() => setDealsPage(p => p - 1)}>← Trước</Button>
                    <Typography variant="body2">Trang {dealsPage} / {dealsTotalPages} ({dealsTotal} lệnh)</Typography>
                    <Button size="small" disabled={dealsPage >= dealsTotalPages} onClick={() => setDealsPage(p => p + 1)}>Tiếp →</Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Line chart — below both tables */}
      <Box sx={{ mt: 3 }}>
        <ProfitChart applied={applied} fetchKey={fetchKey} />
      </Box>
    </>
  );
}

// ─── Tab Lịch sử lỗi ─────────────────────────────────────────────────────────

const ALERT_TYPE_LABELS = { error: 'Lỗi', warning: 'Cảnh báo', info: 'Thông tin', success: 'Thành công' };
const ALERT_TYPE_COLORS = { error: 'error', warning: 'warning', info: 'info', success: 'success' };

/**
 * Phân loại chi tiết alert dựa trên error_type, sync_status và nội dung message.
 * Tất cả logic chạy client-side, không cần thay đổi DB.
 */
function classifyAlert(log) {
  const msg = log.message || '';
  const errType = (log.error_type || '').toUpperCase();
  const syncStatus = (log.sync_status || '').toUpperCase();
  const type = log.alert_type;

  // ── Ưu tiên dùng error_type (từ EA enum) nếu có ──────────────────────────
  if (errType) {
    if (errType === 'ERR_DISCONNECTED')        return 'Mất kết nối';
    if (errType === 'ERR_ALGO_DISABLED')        return 'Algo Trading tắt';
    if (errType === 'ERR_LOW_MARGIN')           return 'Thiếu margin';
    if (errType === 'ERR_SLAVE_EXTRA')          return 'Vị thế thừa';
    if (errType === 'ERR_PENDING_SLAVE_EXTRA')  return 'Lệnh chờ thừa';
    if (errType === 'ERR_COPY_FAILED')          return 'Thiếu vị thế';
    if (errType === 'ERR_PENDING_COPY_FAILED')  return 'Thiếu lệnh chờ';
  }

  // ── sync_status ───────────────────────────────────────────────────────────
  if (syncStatus === 'SYNC_DISCONNECTED')  return 'Mất kết nối';
  if (syncStatus === 'SYNC_ALGO_DISABLED') return 'Algo Trading tắt';
  if (syncStatus === 'SYNC_SLAVE_MORE')    return 'Vị thế thừa';
  if (syncStatus === 'SYNC_MASTER_MORE')   return 'Thiếu vị thế';
  if (syncStatus === 'SYNC_WAITING_MANUAL') return 'Chờ lệnh thủ công';
  if (syncStatus === 'SYNC_OK')            return 'Đồng bộ OK';

  // ── Pattern matching trên nội dung message ────────────────────────────────
  const m = msg.toLowerCase();

  // FIX_ERROR — check đầu tiên để tránh bị pattern bên trong message override
  // VD: "FIX_ERROR: Master mất kết nối" không phải "Mất kết nối"
  if (/fix[_\s]error/.test(m)) {
    if (/th[aà]nh c[oô]ng|success|[dđ][aã] s[uử]a/.test(m)) return 'Sửa lỗi thành công';
    return 'Lệnh FIX_ERROR';
  }

  // Khôi phục kết nối — check TRƯỚC "mất kết nối" vì reconnect message cũng chứa "mất kết nối" (duration text)
  if (/[dđ][aã] k[eế]t n[oố]i l[aạ]i|kh[oô]i ph[uụ]c k[eế]t n[oố]i|ho[aạ]t [dđ][oộ]ng tr[oở] l[aạ]i/.test(m)) return 'Khôi phục kết nối';

  // Mất kết nối
  if (/m[aấ]t k[eế]t n[oố]i|disconnect|kh[oô]ng [dđ][oọ]c [dđ][uư][oợ]c file|heartbeat|kh[oô]ng ho[aạ]t [dđ][oộ]ng/.test(m))
    return 'Mất kết nối';

  // Algo Trading — "bật" trước "tắt" để "không bật algo" không match "bật"
  if (/[dđ][aã] b[aậ]t algo|algo trading.*b[aậ]t|b[aậ]t.*algo trading/.test(m)) return 'Bật Algo Trading';
  if (/algo.*t[aắ]t|t[aắ]t.*algo|algo.*disabled|algo.*off|kh[oô]ng b[aậ]t algo/.test(m)) return 'Algo Trading tắt';

  // Margin / tiền — dùng pattern cụ thể, tránh match plain "margin" trong báo cáo tuần/tháng
  if (/kh[oô]ng [dđ][uủ] ti[eề]n|kh[oô]ng [dđ][uủ] margin|free margin|margin.*th[aấ]p|k[yý] qu[yỹ]/.test(m)) return 'Thiếu margin';

  // ── Lệch VỊ THẾ ──────────────────────────────────────────────────────────
  if (/l[eệ]ch v[iị] th[eế]/.test(m)) {
    if (/slave th[uừ]a|slave.*extra/.test(m)) return 'Vị thế thừa';
    return 'Thiếu vị thế';
  }

  // ── Lệch LỆNH CHỜ ────────────────────────────────────────────────────────
  // EA: "Lệch LỆNH CHỜ - Slave thừa ..." khi Slave > Master
  //     "Lệch LỆNH CHỜ ..." (không có "Slave thừa") khi Master > Slave
  if (/l[eệ]ch l[eệ]nh ch[oờ]/.test(m)) {
    if (/slave th[uừ]a/.test(m)) return 'Lệnh chờ thừa';
    return 'Thiếu lệnh chờ'; // không có "Slave thừa" = Master nhiều lệnh chờ hơn
  }

  // Đồng bộ OK / cân bằng — check trước "thừa" vì message có thể chứa "vị thế"
  if (/[dđ][oồ]ng b[oộ] ok|sync ok|[dđ][aã] c[aâ]n b[aằ]ng|s[oố] l[uư][oợ]ng l[eệ]nh.*[dđ][oồ]ng b[oộ]/.test(m)) return 'Đồng bộ OK';

  // Đã xử lý thành công (kết quả closeextra/copymissing) — check TRƯỚC generic "thừa"
  if (/[dđ][aã] [dđ][oó]ng.*th[uừ]a|[dđ][aã] x[oó]a.*th[uừ]a|[dđ][aã] copy.*thi[eế]u|[dđ][aã] [dđ]óng.*v[iị] th[eế]/.test(m)) return 'Sửa lỗi thành công';

  // Thừa / thiếu chung (fallback cho messages không dùng "Lệch")
  if (/th[uừ]a/.test(m)) {
    if (/l[eệ]nh ch[oờ]|pending/.test(m)) return 'Lệnh chờ thừa';
    return 'Vị thế thừa';
  }
  if (/thi[eế]u|ch[uư]a copy|missing/.test(m)) {
    if (/l[eệ]nh ch[oờ]|pending/.test(m)) return 'Thiếu lệnh chờ';
    return 'Thiếu vị thế';
  }

  // Khối lượng / lot
  if (/l[eệ]ch kh[oố]i l[uư][oợ]ng|l[eệ]ch lot|lot.*l[eệ]ch/.test(m)) return 'Lệch khối lượng';

  // Sửa lỗi (message-based, FIX_ERROR đã check ở trên)
  if (/s[uử]a l[oỗ]i/.test(m)) {
    if (/th[aà]nh c[oô]ng|[dđ][aã] s[uử]a/.test(m)) return 'Sửa lỗi thành công';
    return 'Lệnh FIX_ERROR';
  }

  // Đóng lệnh
  if (/[dđ][oó]ng [aá]ll|close all|close pos/.test(m)) return 'Đóng tất cả lệnh';
  if (/[dđ][oó]ng.*l[eệ]nh|close.*order/.test(m))      return 'Đóng lệnh';

  // Ngoài giờ giao dịch
  if (/ngo[aà]i gi[oờ]|ngoai gio/.test(m)) return 'Ngoài giờ giao dịch';

  // Chờ đồng bộ / chờ lệnh thủ công
  if (/ch[oờ] slave copy|ch[oờ] [dđ][oồ]ng b[oộ]|mapping.*c[aậ]p nh[aậ]t|vua cap nhat/.test(m)) return 'Chờ đồng bộ';
  if (/ch[oờ] v[aà]o l[eệ]nh|th[uủ] c[oô]ng/.test(m)) return 'Chờ lệnh thủ công';

  // Telegram / thông báo
  if (/telegram/.test(m)) return 'Thông báo Telegram';

  // Start / Stop EA — dùng pattern cụ thể hơn để tránh "stop loss" hay "start order"
  if (/kh[oở]i [dđ][oộ]ng.*ea|b[aắ]t [dđ][aầ]u.*ea/.test(m)) return 'Khởi động EA';
  if (/d[uừ]ng ho[aạ]t [dđ][oộ]ng|d[uừ]ng.*ea|copy trade.*t[aắ]t/.test(m)) return 'Dừng EA';

  if (type === 'success') return 'Thành công';
  if (type === 'warning') return 'Cảnh báo chung';
  if (type === 'info')    return 'Thông tin chung';
  return 'Khác';
}

// Danh sách phân loại theo từng loại alert
const CATEGORIES_BY_TYPE = {
  error:   ['Mất kết nối', 'Algo Trading tắt', 'Thiếu margin', 'Vị thế thừa', 'Lệnh chờ thừa', 'Thiếu vị thế', 'Thiếu lệnh chờ', 'Lệch khối lượng', 'Lệnh FIX_ERROR', 'Khác'],
  warning: ['Mất kết nối', 'Algo Trading tắt', 'Thiếu margin', 'Lệnh FIX_ERROR', 'Cảnh báo chung', 'Khác'],
  success: ['Đồng bộ OK', 'Khôi phục kết nối', 'Bật Algo Trading', 'Sửa lỗi thành công', 'Đóng lệnh', 'Đóng tất cả lệnh', 'Thành công', 'Khác'],
  info:    ['Ngoài giờ giao dịch', 'Chờ lệnh thủ công', 'Chờ đồng bộ', 'Lệnh FIX_ERROR', 'Khởi động EA', 'Dừng EA', 'Thông báo Telegram', 'Thông tin chung', 'Khác'],
};
CATEGORIES_BY_TYPE.all = [...new Set(Object.values(CATEGORIES_BY_TYPE).flat())];

function ErrorLogsTab() {
  const { canAccountSeeReport } = useAuth();
  const thStyle = { backgroundColor: 'rgb(15, 21, 53)', color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };
  const PAGE_SIZE = 50;
  const [filters, setFilters] = useState({ from: firstOfMonth(), to: today(), account_id: '', customer_id: '', alert_type: 'all', phan_loai: '' });
  const [applied, setApplied] = useState(filters);
  const [fetchKey, setFetchKey] = useState(0);
  const [displayPage, setDisplayPage] = useState(1);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const queryClient = useQueryClient();

  const { data: customerOptions = [] } = useQuery('customer-options-el', () =>
    customersAPI.getOptions().then(r => r.data).catch(() => [])
  );

  const handleDelete = useCallback(async (id) => {
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      await mt5API.deleteAlert(id);
      queryClient.invalidateQueries('error-logs');
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, [queryClient]);

  const { data: accountsData } = useQuery('accounts-list-el', () =>
    statusAPI.getAccounts().then(r => r.data)
  );
  // Filter dropdown: viewer's per-account override may forbid this sub-tab.
  const accounts = (accountsData?.accounts || []).filter(a => canAccountSeeReport(a.mt5_account_id, 'error_logs'));

  const { data, isLoading, isError } = useQuery(
    ['error-logs', applied, fetchKey],
    () => reportsAPI.getErrorLogs({
      from: applied.from || undefined,
      to: applied.to || undefined,
      account_id: applied.account_id || undefined,
      customer_id: applied.customer_id || undefined,
      alert_type: applied.alert_type !== 'all' ? applied.alert_type : undefined,
      page: 1,
      limit: 1000,
    }),
    { keepPreviousData: true }
  );

  const allLogs = data?.data?.logs || [];
  const total = data?.data?.total || 0;

  // Client-side filter by phan_loai
  const filteredLogs = applied.phan_loai
    ? allLogs.filter(l => classifyAlert(l) === applied.phan_loai)
    : allLogs;

  const totalPages = Math.ceil(filteredLogs.length / PAGE_SIZE);
  const pageLogs = filteredLogs.slice((displayPage - 1) * PAGE_SIZE, displayPage * PAGE_SIZE);

  // Options for Phân loại dropdown based on selected alert_type
  const phanLoaiOptions = CATEGORIES_BY_TYPE[filters.alert_type === 'all' ? 'all' : filters.alert_type] || CATEGORIES_BY_TYPE.all;

  const handleSearch = () => {
    setApplied({ ...filters });
    setDisplayPage(1);
    setFetchKey(k => k + 1);
  };

  const handleExportCSV = () => {
    if (!filteredLogs.length) return;
    const headers = ['STT', 'Thời gian', 'Tài khoản', 'Tên', 'Loại', 'Phân loại', 'Tin nhắn', 'Nguồn'];
    const rows = filteredLogs.map((l, i) => [
      i + 1,
      new Date(l.received_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }),
      l.slave_account_id || '-',
      l.client_name || l.slave_name || '-',
      ALERT_TYPE_LABELS[l.alert_type] || l.alert_type || '-',
      classifyAlert(l),
      l.message || '-',
      l.source || '-',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error_logs_${applied.from}_${applied.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" label="Từ ngày" type="date"
              value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" label="Đến ngày" type="date"
              value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tài khoản</InputLabel>
              <Select value={filters.account_id} label="Tài khoản"
                onChange={e => setFilters(f => ({ ...f, account_id: e.target.value }))}>
                <MenuItem value="">-- Tất cả --</MenuItem>
                {accounts.map(a => (
                  <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                    {a.client_name ? `${a.client_name} (${a.mt5_account_id})` : a.mt5_account_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {customerOptions.length > 0 && (
            <Grid item xs={12} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Khách hàng</InputLabel>
                <Select value={filters.customer_id} label="Khách hàng"
                  onChange={e => setFilters(f => ({ ...f, customer_id: e.target.value }))}>
                  <MenuItem value="">-- Tất cả --</MenuItem>
                  {customerOptions.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Loại</InputLabel>
              <Select value={filters.alert_type} label="Loại"
                onChange={e => setFilters(f => ({ ...f, alert_type: e.target.value, phan_loai: '' }))}>
                <MenuItem value="all">-- Tất cả --</MenuItem>
                <MenuItem value="error">Lỗi</MenuItem>
                <MenuItem value="warning">Cảnh báo</MenuItem>
                <MenuItem value="info">Thông tin</MenuItem>
                <MenuItem value="success">Thành công</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Phân loại</InputLabel>
              <Select value={filters.phan_loai} label="Phân loại"
                onChange={e => setFilters(f => ({ ...f, phan_loai: e.target.value }))}>
                <MenuItem value="">-- Tất cả --</MenuItem>
                {phanLoaiOptions.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2} md={1}>
            <Button fullWidth variant="contained" onClick={handleSearch}>Lọc</Button>
          </Grid>
          <Grid item xs={6} sm={2} md={1}>
            <Button fullWidth variant="outlined" onClick={handleExportCSV} disabled={!filteredLogs.length}>CSV</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary */}
      {!isLoading && !isError && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['error', 'warning', 'info', 'success'].map(type => {
            const count = filteredLogs.filter(l => l.alert_type === type).length;
            if (!count) return null;
            return (
              <Chip key={type} size="small"
                label={`${ALERT_TYPE_LABELS[type]}: ${count}`}
                color={ALERT_TYPE_COLORS[type]} variant="outlined" />
            );
          })}
          <Chip size="small" label={`Tổng: ${filteredLogs.length}${applied.phan_loai ? ` (lọc từ ${total})` : ` bản ghi`}`} variant="outlined" />
        </Box>
      )}

      {/* Table */}
      <Paper>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : isError ? (
          <Alert severity="error" sx={{ m: 2 }}>Lỗi khi tải dữ liệu</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ ...thStyle, width: 50 }}>STT</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 140 }}>Thời gian</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 100 }}>Tài khoản</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 105 }}>Tên</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 90 }}>Loại</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 140 }}>Phân loại</TableCell>
                    <TableCell sx={{ ...thStyle }}>Tin nhắn</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 75 }}>Nguồn</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 44 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Không có dữ liệu
                      </TableCell>
                    </TableRow>
                  ) : pageLogs.map((l, i) => (
                    <TableRow key={l.id} hover>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {(displayPage - 1) * PAGE_SIZE + i + 1}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {new Date(l.received_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{l.slave_account_id || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.client_name || l.slave_name || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={ALERT_TYPE_LABELS[l.alert_type] || l.alert_type || '-'}
                          color={ALERT_TYPE_COLORS[l.alert_type] || 'default'}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                        {classifyAlert(l)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', wordBreak: 'break-word' }}>{l.message || '-'}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{l.source || '-'}</TableCell>
                      <TableCell align="center" sx={{ p: 0.5 }}>
                        <Tooltip title="Xóa log này">
                          <IconButton
                            size="small"
                            disabled={deletingIds.has(l.id)}
                            onClick={() => handleDelete(l.id)}
                            sx={{ opacity: 0.35, '&:hover': { opacity: 1, color: 'error.main' }, transition: 'opacity 0.15s' }}
                          >
                            <CloseIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, p: 2 }}>
                  <Button size="small" disabled={displayPage <= 1}
                    onClick={() => setDisplayPage(p => p - 1)}>← Trước</Button>
                  <Typography variant="body2">Trang {displayPage} / {totalPages} ({filteredLogs.length} bản ghi)</Typography>
                  <Button size="small" disabled={displayPage >= totalPages}
                    onClick={() => setDisplayPage(p => p + 1)}>Tiếp →</Button>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  );
}

// ─── Tab Lịch sử thông số ─────────────────────────────────────────────────────

function SetupHistoryTab() {
  const { canAccountSeeReport } = useAuth();
  const thStyle = { backgroundColor: 'rgb(15, 21, 53)', color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };
  const [filters, setFilters] = useState({ from: firstOfMonth(), to: today(), account_id: '', customer_id: '', page: 1, limit: 50 });
  const [applied, setApplied] = useState(filters);
  const [fetchKey, setFetchKey] = useState(0);

  const { data: accountsData } = useQuery('accounts-list-sh', () =>
    statusAPI.getAccounts().then(r => r.data)
  );
  // Filter dropdown: viewer's per-account override may forbid this sub-tab.
  const accounts = (accountsData?.accounts || []).filter(a => canAccountSeeReport(a.mt5_account_id, 'setup_history'));

  const { data: customerOptions = [] } = useQuery('customer-options-sh', () =>
    customersAPI.getOptions().then(r => r.data).catch(() => [])
  );

  const { data, isLoading, isError } = useQuery(
    ['setup-history', applied, fetchKey],
    () => reportsAPI.getSetupHistory({
      from: applied.from || undefined,
      to: applied.to || undefined,
      account_id: applied.account_id || undefined,
      customer_id: applied.customer_id || undefined,
      page: applied.page,
      limit: applied.limit,
    }),
    { keepPreviousData: true }
  );

  const rows = data?.data?.rows || [];
  const total = data?.data?.total || 0;
  const totalPages = Math.ceil(total / applied.limit);

  const handleSearch = () => {
    setApplied({ ...filters, page: 1 });
    setFetchKey(k => k + 1);
  };

  const fmtRatio = (v) => v || '-';
  const fmtLoss = (v) => (v != null && v !== 0) ? `$${parseFloat(v).toFixed(2)}` : '-';
  const fmtShare = (v) => (v != null && v !== 0) ? `${v}%` : '-';

  const handleExportCSV = () => {
    if (!rows.length) return;
    const headers = ['STT', 'Thời gian', 'Tài khoản', 'Tên', 'Tỷ lệ cũ', 'Tỷ lệ mới', 'SL cứng cũ', 'SL cứng mới', 'Tỷ lệ LN cũ', 'Tỷ lệ LN mới'];
    const csvRows = rows.map((r, i) => [
      (applied.page - 1) * applied.limit + i + 1,
      new Date(r.changed_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }),
      r.mt5_account_id,
      r.client_name || '-',
      fmtRatio(r.prev_copy_ratio),
      fmtRatio(r.copy_ratio),
      fmtLoss(r.prev_max_loss),
      fmtLoss(r.max_loss),
      fmtShare(r.prev_profit_share),
      fmtShare(r.profit_share),
    ]);
    const csv = [headers, ...csvRows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `setup_history_${applied.from}_${applied.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" label="Từ ngày" type="date"
              value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" label="Đến ngày" type="date"
              value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tài khoản</InputLabel>
              <Select value={filters.account_id} label="Tài khoản"
                onChange={e => setFilters(f => ({ ...f, account_id: e.target.value }))}>
                <MenuItem value="">-- Tất cả --</MenuItem>
                {accounts.map(a => (
                  <MenuItem key={a.mt5_account_id} value={a.mt5_account_id}>
                    {a.client_name ? `${a.client_name} (${a.mt5_account_id})` : a.mt5_account_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {customerOptions.length > 0 && (
            <Grid item xs={12} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Khách hàng</InputLabel>
                <Select value={filters.customer_id} label="Khách hàng"
                  onChange={e => setFilters(f => ({ ...f, customer_id: e.target.value }))}>
                  <MenuItem value="">-- Tất cả --</MenuItem>
                  {customerOptions.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={6} sm={2} md={1}>
            <Button fullWidth variant="contained" onClick={handleSearch}>Lọc</Button>
          </Grid>
          <Grid item xs={6} sm={2} md={1}>
            <Button fullWidth variant="outlined" onClick={handleExportCSV} disabled={!rows.length}>CSV</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : isError ? (
          <Alert severity="error" sx={{ m: 2 }}>Lỗi khi tải dữ liệu</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ ...thStyle, width: 50 }}>STT</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 145 }}>Thời gian</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 105 }}>Tài khoản</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 120 }}>Tên</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 110 }}>Tỷ lệ copy cũ</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 110 }}>Tỷ lệ copy mới</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 110 }}>SL cứng cũ</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 110 }}>SL cứng mới</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 100 }}>Tỷ lệ LN cũ</TableCell>
                    <TableCell align="center" sx={{ ...thStyle, width: 100 }}>Tỷ lệ LN mới</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Không có dữ liệu
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r, i) => {
                    const ratioChanged = r.copy_ratio !== r.prev_copy_ratio;
                    const lossChanged = r.max_loss !== r.prev_max_loss;
                    const shareChanged = r.profit_share !== r.prev_profit_share;
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell align="center" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {(applied.page - 1) * applied.limit + i + 1}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {new Date(r.changed_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{r.mt5_account_id}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.client_name || '-'}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.8rem', color: ratioChanged ? '#ef4444' : 'inherit' }}>
                          {fmtRatio(r.prev_copy_ratio)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: ratioChanged ? 700 : 400, color: ratioChanged ? '#22c55e' : 'inherit' }}>
                          {fmtRatio(r.copy_ratio)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.8rem', color: lossChanged ? '#ef4444' : 'inherit' }}>
                          {fmtLoss(r.prev_max_loss)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: lossChanged ? 700 : 400, color: lossChanged ? '#22c55e' : 'inherit' }}>
                          {fmtLoss(r.max_loss)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.8rem', color: shareChanged ? '#ef4444' : 'inherit' }}>
                          {fmtShare(r.prev_profit_share)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: shareChanged ? 700 : 400, color: shareChanged ? '#22c55e' : 'inherit' }}>
                          {fmtShare(r.profit_share)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, p: 2 }}>
                  <Button size="small" disabled={applied.page <= 1}
                    onClick={() => setApplied(a => ({ ...a, page: a.page - 1 }))}>← Trước</Button>
                  <Typography variant="body2">Trang {applied.page} / {totalPages} ({total} bản ghi)</Typography>
                  <Button size="small" disabled={applied.page >= totalPages}
                    onClick={() => setApplied(a => ({ ...a, page: a.page + 1 }))}>Tiếp →</Button>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  );
}

// ─── Main Reports Page ─────────────────────────────────────────────────────────

export default function Reports() {
  const { canSeeReport } = useAuth();

  // Build the visible-tabs list once per render. REPORTS_SUB_TABS keeps order
  // aligned with REPORTS_TAB_ORDER so tab keys stay stable.
  const visibleTabs = REPORTS_SUB_TABS.filter(t => canSeeReport(t.key));
  const visibleKeys = visibleTabs.map(t => t.key);

  // Restore previously-selected tab key from localStorage; fall back to first
  // allowed tab if the saved key isn't permitted (e.g. permissions changed).
  const [tabKey, setTabKey] = useState(() => {
    const saved = localStorage.getItem('reports_tab_key');
    if (saved && visibleKeys.includes(saved)) return saved;
    // Legacy: previously stored as numeric index — translate once.
    const legacyIdx = localStorage.getItem('reports_tab');
    if (legacyIdx !== null) {
      const idx = parseInt(legacyIdx);
      const legacyKey = REPORTS_TAB_ORDER[idx];
      if (legacyKey && visibleKeys.includes(legacyKey)) return legacyKey;
    }
    return visibleKeys[0] || REPORTS_TAB_ORDER[0];
  });

  // Re-sync if permission set changes and current tab is no longer allowed.
  if (visibleKeys.length > 0 && !visibleKeys.includes(tabKey)) {
    setTabKey(visibleKeys[0]);
  }

  const handleTabChange = (_, v) => {
    setTabKey(v);
    localStorage.setItem('reports_tab_key', v);
  };

  if (visibleKeys.length === 0) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Báo cáo</Typography>
        <Alert severity="info">Bạn không có quyền xem báo cáo nào.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Báo cáo</Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabKey}
          onChange={handleTabChange}
          sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
        >
          {visibleTabs.map(t => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {tabKey === 'transactions'  && <TransactionsTab />}
      {tabKey === 'profit'        && <ProfitTab />}
      {tabKey === 'setup_history' && <SetupHistoryTab />}
      {tabKey === 'error_logs'    && <ErrorLogsTab />}
    </Box>
  );
}
